import React from 'react';
import { Link } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';

const BPO = () => {
  return (
    <>
      <Helmet>
        <title>BPO (Business Process Outsourcing) - ZiExter | Externalisation back-office</title>
        <meta name="description" content="Externalisez vos processus avec ZiExter : e-commerce, traitement image/médias, développement IT, community management, relation client. Solutions BPO sur-mesure." />
        <link rel="canonical" href="https://ziexter.com/services/bpo" />
      </Helmet>

      {/* Hero */}
      <section className="page-hero bg-gradient-to-br from-secondary to-primary text-white">
        <div className="container-custom">
          <div className="max-w-3xl animate-fade-in-up">
            <nav className="flex items-center space-x-2 text-sm mb-4 text-gray-200">
              <Link to="/" className="hover:text-white">Accueil</Link>
              <span>/</span>
              <Link to="/services" className="hover:text-white">Services</Link>
              <span>/</span>
              <span className="text-white">BPO</span>
            </nav>
            <h1 className="font-heading text-4xl md:text-5xl font-bold mb-6">
              BPO & Back Office – ZiExter
            </h1>
            <p className="text-xl text-gray-100">
              Chez ZiExter, nous réinventons la manière dont les entreprises gèrent et optimisent leurs processus métiers.
            </p>
            <p className="text-lg text-gray-200 mt-4">
              Notre expertise en Business Process Outsourcing (BPO) vous permet de confier vos tâches répétitives ou stratégiques à une équipe spécialisée, afin de gagner en productivité, en agilité et en rentabilité.
            </p>
            <p className="text-lg text-gray-200 mt-4">
              Avec ZiExter, vous ne déléguez pas seulement vos opérations : vous vous associez à un partenaire qui fait de votre réussite sa priorité.
            </p>
          </div>
        </div>
      </section>

      {/* CTA Hero */}
      <section className="py-8 bg-accent">
        <div className="container-custom">
          <div className="flex flex-col sm:flex-row gap-4 justify-center items-center">
            <Link to="/devis" className="btn btn-secondary">
              Demandez un devis gratuit
            </Link>
            <Link to="/rendez-vous" className="btn btn-outline-white">
              Planifiez une consultation personnalisée
            </Link>
          </div>
        </div>
      </section>

      {/* Nos solutions BPO */}
      <section className="section-padding bg-white">
        <div className="container-custom">
          <div className="max-w-3xl mx-auto text-center mb-12 animate-fade-in-up">
            <h2 className="section-title">Nos solutions BPO sur-mesure</h2>
          </div>

          <div className="space-y-12">
            {/* E-commerce */}
            <div className="card animate-fade-in-up">
              <h3 className="text-2xl font-bold mb-4">1. E-commerce</h3>
              <ul className="space-y-2 text-gray-700">
                <li className="flex items-start">
                  <span className="text-primary mr-2">•</span>
                  <strong>Veille marketing et concurrentielle :</strong> suivi permanent des tendances du marché et des médias pour nourrir vos décisions stratégiques.
                </li>
                <li className="flex items-start">
                  <span className="text-primary mr-2">•</span>
                  <strong>Fiches produits optimisées :</strong> création de fiches détaillées et attractives pour valoriser vos produits et booster vos ventes.
                </li>
                <li className="flex items-start">
                  <span className="text-primary mr-2">•</span>
                  <strong>Rédaction SEO & brand content :</strong> contenus engageants et optimisés pour améliorer votre visibilité sur les moteurs de recherche.
                </li>
                <li className="flex items-start">
                  <span className="text-primary mr-2">•</span>
                  <strong>Gestion et qualification de bases de données (BDD) :</strong> exploitation ciblée et fiable de vos ressources pour un meilleur ROI.
                </li>
              </ul>
            </div>

            {/* Traitement de l'image */}
            <div className="card animate-fade-in-up animation-delay-100">
              <h3 className="text-2xl font-bold mb-4">2. Traitement de l'image & des médias</h3>
              <ul className="space-y-2 text-gray-700">
                <li className="flex items-start">
                  <span className="text-primary mr-2">•</span>
                  <strong>Création visuelle & retouches :</strong> photos produits, visuels impactants et mise en valeur professionnelle de vos supports.
                </li>
                <li className="flex items-start">
                  <span className="text-primary mr-2">•</span>
                  <strong>Montage et post-production vidéo :</strong> un rendu qualitatif et adapté à vos canaux de diffusion.
                </li>
                <li className="flex items-start">
                  <span className="text-primary mr-2">•</span>
                  <strong>Organisation des médias :</strong> découpage, cadrage, indexation et catégorisation pour un accès rapide et structuré.
                </li>
              </ul>
            </div>

            {/* Développement IT */}
            <div className="card animate-fade-in-up animation-delay-200">
              <h3 className="text-2xl font-bold mb-4">3. Développement IT & Testing Q/A</h3>
              <ul className="space-y-2 text-gray-700">
                <li className="flex items-start">
                  <span className="text-primary mr-2">•</span>
                  <strong>Intégration de données multiples :</strong> XML, HTML, CSV, TXT, DB – pour une cohérence et une fluidité sans faille.
                </li>
                <li className="flex items-start">
                  <span className="text-primary mr-2">•</span>
                  <strong>Tests de conformité produits :</strong> vérification rigoureuse selon votre cahier des charges pour garantir qualité et fiabilité.
                </li>
              </ul>
            </div>

            {/* Community Management */}
            <div className="card animate-fade-in-up animation-delay-300">
              <h3 className="text-2xl font-bold mb-4">4. Community Management & E-réputation</h3>
              <ul className="space-y-2 text-gray-700">
                <li className="flex items-start">
                  <span className="text-primary mr-2">•</span>
                  <strong>Modération & animation de contenu :</strong> présence en ligne maîtrisée et engageante.
                </li>
                <li className="flex items-start">
                  <span className="text-primary mr-2">•</span>
                  <strong>Gestion de vos réseaux sociaux et forums :</strong> interaction continue avec vos communautés pour renforcer votre image.
                </li>
                <li className="flex items-start">
                  <span className="text-primary mr-2">•</span>
                  <strong>E-réputation proactive :</strong> stratégies de protection et de valorisation de votre marque en ligne.
                </li>
                <li className="flex items-start">
                  <span className="text-primary mr-2">•</span>
                  <strong>Analyse des verbatims clients :</strong> extraction d'insights précieux pour améliorer votre stratégie relationnelle.
                </li>
              </ul>
            </div>

            {/* Relation Client */}
            <div className="card animate-fade-in-up animation-delay-400">
              <h3 className="text-2xl font-bold mb-4">5. Relation Client – Une équipe dédiée</h3>
              <p className="text-lg text-gray-700 mb-4">
                Notre différence ? Nous ne proposons pas seulement de l'externalisation :
              </p>
              <p className="text-lg font-semibold text-primary mb-4">
                Nous constituons une équipe dédiée, façonnée pour devenir une véritable extension de votre entreprise.
              </p>
              <ul className="space-y-2 text-gray-700">
                <li className="flex items-start">
                  <span className="text-primary mr-2">•</span>
                  Même qualité qu'une équipe interne.
                </li>
                <li className="flex items-start">
                  <span className="text-primary mr-2">•</span>
                  Processus structurés et sur-mesure.
                </li>
                <li className="flex items-start">
                  <span className="text-primary mr-2">•</span>
                  Objectifs alignés sur votre stratégie de croissance.
                </li>
              </ul>
              <p className="text-lg text-gray-700 mt-4">
                Avec ZiExter, votre back office est entre de bonnes mains, et votre relation client s'élève à un nouveau niveau d'excellence.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Ils nous font confiance */}
      <section className="section-padding bg-gray-50">
        <div className="container-custom">
          <div className="max-w-3xl mx-auto text-center mb-12 animate-fade-in-up">
            <h2 className="section-title">Ils nous font confiance</h2>
            <p className="text-lg text-gray-600">
              La confiance de nos clients est notre meilleure preuve de réussite. Des PME aux grands comptes, nos collaborations témoignent de notre engagement envers l'excellence opérationnelle et la satisfaction client.
            </p>
            <p className="text-sm text-gray-500 mt-4">(ici, possibilité d'intégrer logos et témoignages clients)</p>
          </div>
        </div>
      </section>

      {/* Pourquoi choisir ZiExter */}
      <section className="section-padding bg-white">
        <div className="container-custom">
          <div className="max-w-3xl mx-auto text-center mb-12 animate-fade-in-up">
            <h2 className="section-title">Pourquoi choisir ZiExter pour votre BPO ?</h2>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
            {[
              '✅ Réduction des coûts opérationnels.',
              '✅ Accélération de vos délais de production.',
              '✅ Expertise multi-sectorielle.',
              '✅ Service 100% flexible et adapté à vos besoins.'
            ].map((item, i) => (
              <div key={i} className="card hover-lift text-center animate-fade-in-up" style={{ animationDelay: `${i * 50}ms` }}>
                <p className="text-lg font-semibold text-gray-700">{item}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Final */}
      <section className="section-padding bg-gradient-to-r from-primary to-secondary text-white">
        <div className="container-custom">
          <div className="max-w-3xl mx-auto text-center animate-fade-in-up">
            <h2 className="font-heading text-3xl md:text-4xl font-bold mb-6">
              Vous partagez notre obsession pour la performance et la satisfaction client ?
            </h2>
            <p className="text-xl text-gray-100 mb-8">
              Rencontrons-nous et construisons ensemble votre stratégie BPO sur mesure.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Link to="/contact" className="btn btn-accent btn-lg">
                Contactez-nous dès aujourd'hui !
              </Link>
              <Link to="/devis" className="btn btn-outline-white btn-lg">
                Demander un devis
              </Link>
            </div>
          </div>
        </div>
      </section>
    </>
  );
};

export default BPO;
